import { Theme, alpha, createTheme } from '@mui/material/styles';

import {
  BRAND_COLORS,
  BRAND_DARK_MODE,
  FONT_STACK_SUISSE_INTL,
  FONT_STACK_SUISSE_WORKS,
} from '~/components/brand/brandTokens';

/**
 * Design tokens shared by the unified analytics surfaces (AskRichardUnified shell
 * and the LE Lens canvas it hosts). The admin MUI theme stays the substrate so
 * dark mode and every existing component keep working; these tokens apply the
 * Luxury Escapes 2026 brand and are the only place raw colour values are
 * allowed to live.
 *
 * `accent`, `threadAccent` and `highlight` are structural (Ocean Teal in light,
 * Compass White in dark) and must never resolve to Coral Green: that colour is
 * reserved for `coralAccent`, used only for the wordmark stop, the Lens dot, one
 * highlighted chart series or marker, and large display figures, never a
 * button, chip or thread background.
 */

export const LENS_TOKENS = {
  light: {
    canvas: BRAND_COLORS.sandyBeige,
    surface: BRAND_COLORS.compassWhite,
    surfaceSunken: '#F3F1EA',
    // The AskRichard rail remains dark teal: it is the established navigation
    // anchor, while Ocean Teal leads actions inside the workspace.
    rail: BRAND_COLORS.oceanTeal,
    railHover: 'rgba(255, 255, 255, 0.07)',
    railActive: 'rgba(255, 255, 255, 0.11)',
    railBorder: 'rgba(255, 255, 255, 0.14)',
    railText: 'rgba(255, 255, 255, 0.96)',
    railTextMuted: 'rgba(255, 255, 255, 0.64)',
    accent: BRAND_COLORS.oceanTeal,
    accentContrast: BRAND_COLORS.compassWhite,
    /** Ocean Teal carries the primary interaction through chat and Lens alike. */
    threadAccent: BRAND_COLORS.oceanTeal,
    /** Selection and the rail's New Thread action use the same structural Ocean Teal, never the accent mint. */
    highlight: BRAND_COLORS.oceanTeal,
    /** The wordmark stop, the Lens dot, one highlighted chart series or marker, large display figures. Never a fill. */
    coralAccent: BRAND_COLORS.coralGreen,
    /** Editorial landing-page band. Detail and conversation surfaces stay flat. */
    band: BRAND_COLORS.oceanTeal,
    bandTexture: 'rgba(255, 255, 255, 0.06)',
    plateHover: 'rgba(15, 70, 79, 0.04)',
    dotGrid: 'rgba(15, 70, 79, 0.06)',
    /** Reserved for a future landing treatment; intentionally unused in pass three. */
    wash: 'radial-gradient(circle at top left, rgba(15, 70, 79, 0.05), rgba(15, 70, 79, 0) 40%)',
    certifiedBg: '#D5FCE2',
    certifiedText: '#0B5B36',
    provisionalBg: '#FBEFD9',
    provisionalText: '#8A5A16',
    blockedBg: '#FBE0E0',
    blockedText: '#8C1D1D',
    truncatedBg: '#FBEFD9',
    truncatedBorder: '#E49F4E',
    grid: '#EBE7DE',
    axis: '#DEDAD0',
  },
  dark: {
    // The field is Ocean Teal, not black: the 2026 dark-mode brand pair.
    canvas: BRAND_DARK_MODE.field,
    // A card on that teal field is a lighter teal, never black.
    surface: BRAND_DARK_MODE.card,
    surfaceSunken: '#0F0C08',
    rail: '#0B3339',
    railHover: 'rgba(255, 255, 255, 0.07)',
    railActive: 'rgba(255, 255, 255, 0.12)',
    railBorder: 'rgba(255, 255, 255, 0.16)',
    railText: 'rgba(255, 255, 255, 0.96)',
    railTextMuted: 'rgba(255, 255, 255, 0.66)',
    // Compass White, not the accent mint: Coral Green never stands in as a fill or as
    // structural type/interaction colour, in either mode (2026 brand rule).
    accent: BRAND_COLORS.compassWhite,
    accentContrast: BRAND_COLORS.jetBlack,
    /** Keeps chat controls visible against the dark conversation surface. */
    threadAccent: BRAND_COLORS.compassWhite,
    highlight: BRAND_COLORS.compassWhite,
    /** The wordmark stop, the Lens dot, one highlighted chart series or marker, large display figures. Never a fill. */
    coralAccent: BRAND_COLORS.coralGreen,
    /** The catalogue front door flips polarity in dark mode. */
    band: BRAND_COLORS.sandyBeige,
    bandTexture: 'rgba(15, 70, 79, 0.06)',
    plateHover: 'rgba(255, 255, 255, 0.04)',
    dotGrid: 'rgba(255, 255, 255, 0.06)',
    /** Reserved for a future landing treatment; intentionally unused in pass three. */
    wash: 'radial-gradient(circle at top left, rgba(249, 247, 241, 0.05), rgba(249, 247, 241, 0) 40%)',
    certifiedBg: 'rgba(16, 185, 129, 0.16)',
    certifiedText: '#6EE7B7',
    provisionalBg: 'rgba(228, 159, 78, 0.16)',
    provisionalText: '#E7B173',
    blockedBg: 'rgba(212, 48, 68, 0.18)',
    blockedText: '#F0979F',
    truncatedBg: 'rgba(228, 159, 78, 0.14)',
    truncatedBorder: '#B87D3C',
    grid: 'rgba(255, 255, 255, 0.2)',
    axis: 'rgba(255, 255, 255, 0.42)',
  },
} as const;

export type LensTokens = { [Key in keyof typeof LENS_TOKENS.light]: string };

/**
 * Sequential ramp for a heatmap cell. One hue with monotone lightness, so the
 * order of the steps survives greyscale and every form of colour blindness:
 * a categorical palette used as a ramp does not, which is why a heatmap never
 * reuses the chart series palette (owned by LeLens/lensChart.ts).
 */
export const HEAT_RAMP_LIGHT = [
  'rgba(15, 70, 79, 0.16)',
  'rgba(15, 70, 79, 0.30)',
  'rgba(15, 70, 79, 0.46)',
  'rgba(15, 70, 79, 0.62)',
  'rgba(15, 70, 79, 0.78)',
] as const;

export const HEAT_RAMP_DARK = ['#2A544C', '#317A6B', '#3C9E88', '#46C6A4', '#4EFEB9'] as const;

export function heatRamp(isDark: boolean): ReadonlyArray<string> {
  return isDark ? HEAT_RAMP_DARK : HEAT_RAMP_LIGHT;
}

export function lensTokens(theme: Theme): LensTokens {
  return theme.palette.mode === 'dark' ? LENS_TOKENS.dark : LENS_TOKENS.light;
}

export const LENS_LAYOUT = {
  railWidth: 280,
  /** Icon strip when the rail gives way to the right pane. */
  railCollapsedWidth: 60,
  /** Quiet, technical surfaces: rectangular enough to make the data the focus. */
  cardRadius: 2,
  radiusPlate: 20,
  radiusCard: 16,
  inputRadius: 2,
  cardMinWidth: 200,
  /** One spacing step between related dashboard cards and groups. */
  gridGap: 8,
  sectionLanding: 96,
  sectionList: 56,
  sectionDetail: 56,
  /** Minimum gap between adjacent genuine pills: one quarter of a 32px pill. */
  pillGap: 8,
  chartHeightCompact: 170,
  chartHeightRegular: 210,
  chartHeightTall: 280,
  /** Above this row count a chart renders a sampled view rather than every mark. */
  chartMarkCeiling: 3000,
} as const;

/**
 * Card states are visually distinct by construction. A card is never allowed to
 * present a partial result as a finished number, so `truncated` suppresses totals.
 */
export type LensCardState = 'loading' | 'ready' | 'empty' | 'error' | 'truncated' | 'restricted';

/**
 * The editorial-page surface system (Business Pulse, Using data, the Data Catalogue and Lens
 * library): one small radius, one hairline formula, one row height. This is distinct from
 * `LENS_LAYOUT.cardRadius` (the global MUI shape radius applied to every button/card/paper in the
 * admin app) and from `LENS_LAYOUT.radiusCard`/`radiusPlate` (the larger-radius system used by
 * Lens panel and library cards). Values are read from Business Pulse's own code, not invented; see
 * DESIGN_LANGUAGE.md for the file:line citation behind each one.
 */
export const SURFACE = {
  radius: 8,
  /** Business Pulse's existing header plate only (HeroBand.tsx). Never reuse for a new plate. */
  headerPlateRadius: 10,
  /** Outer plate/card border, matches Business Pulse's domain cards and metric strip. */
  hairlineAlpha: 0.14,
  /** Internal row/divider separators within a plate. */
  hairlineAlphaSubtle: 0.12,
  /** A bigger clickable area, such as a domain/area card. */
  cardPadding: 20,
  /** A compact info panel; the default padding variant. */
  platePadding: 18,
  rowHeight: LENS_LAYOUT.sectionList,
  inputHeight: 44,
  headerToSectionGap: 24,
  sectionGap: 40,
} as const;

/** The one hairline formula for this surface system: `subtle` picks the internal-divider alpha. */
export function surfaceHairline(tokens: LensTokens, subtle = false): string {
  return `1px solid ${alpha(tokens.accent, subtle ? SURFACE.hairlineAlphaSubtle : SURFACE.hairlineAlpha)}`;
}

export function buildUnifiedAnalyticsTheme(base: Theme): Theme {
  const tokens = base.palette.mode === 'dark' ? LENS_TOKENS.dark : LENS_TOKENS.light;
  return createTheme(base, {
    shape: { borderRadius: LENS_LAYOUT.cardRadius },
    palette: {
      background: { default: tokens.canvas, paper: tokens.surface },
      // The MUI primary slot is always Ocean Teal with Compass White contrast
      // text, in both modes: this is the formal brand primary, distinct from
      // the mode-tuned `tokens.accent` used for bespoke component styling
      // elsewhere in the workspace.
      primary: { main: BRAND_COLORS.oceanTeal, contrastText: BRAND_COLORS.compassWhite },
      // MUI's default info state is blue. In this workspace, informational
      // guidance is part of the same calm teal system as the rest of the UI.
      info: {
        main: tokens.accent,
        light: alpha(tokens.accent, base.palette.mode === 'dark' ? 0.22 : 0.14),
        dark: tokens.accent,
        contrastText: tokens.accentContrast,
      },
      text:
        base.palette.mode === 'dark'
          ? { primary: BRAND_COLORS.compassWhite, secondary: 'rgba(255, 255, 255, 0.78)' }
          : { ...base.palette.text, primary: BRAND_COLORS.jetBlack, secondary: alpha(BRAND_COLORS.jetBlack, 0.72) },
    },
    typography: {
      // createTheme(base, options) merges into base's already-computed typography
      // variants rather than recomputing them from a new root fontFamily, so every
      // variant needs its own explicit fontFamily here: the root value alone does
      // not cascade to body1, caption, button and so on when extending a full
      // theme this way.
      fontFamily: FONT_STACK_SUISSE_INTL,
      // Display type only: dashboard and page titles. Suisse Works per 01_SPEC.md.
      h1: { fontFamily: FONT_STACK_SUISSE_WORKS, fontWeight: 400, letterSpacing: '-0.025em' },
      h2: { fontFamily: FONT_STACK_SUISSE_WORKS, fontWeight: 400, letterSpacing: '-0.025em' },
      h3: { fontFamily: FONT_STACK_SUISSE_WORKS, fontWeight: 400, letterSpacing: '-0.02em' },
      // Everything else is Suisse Int'l, including the smaller headings used for
      // card and panel titles.
      h4: { fontFamily: FONT_STACK_SUISSE_INTL, fontWeight: 500, letterSpacing: '-0.025em' },
      h5: { fontFamily: FONT_STACK_SUISSE_INTL, fontWeight: 500, letterSpacing: '-0.02em' },
      // Card title per 01_SPEC.md's type scale: LensCardShell's non-compact title
      // renders through this variant (compact renders through subtitle2 above,
      // sized to match), and every other h6 in the unified shell and Lens is a
      // dialog title or empty-state heading, which reads fine at this size too.
      h6: {
        fontFamily: FONT_STACK_SUISSE_INTL,
        fontWeight: 600,
        fontSize: '15px',
        lineHeight: 1.3,
        letterSpacing: '-0.012em',
      },
      subtitle1: { fontFamily: FONT_STACK_SUISSE_INTL },
      // Card title: 15px SemiBold per 01_SPEC.md's type scale.
      subtitle2: { fontFamily: FONT_STACK_SUISSE_INTL, fontWeight: 600, letterSpacing: '-0.005em', fontSize: '15px' },
      body1: { fontFamily: FONT_STACK_SUISSE_INTL },
      body2: { fontFamily: FONT_STACK_SUISSE_INTL },
      caption: { fontFamily: FONT_STACK_SUISSE_INTL },
      button: { fontFamily: FONT_STACK_SUISSE_INTL },
      overline: { fontFamily: FONT_STACK_SUISSE_INTL },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: LENS_LAYOUT.cardRadius,
            backgroundColor: tokens.surface,
            backgroundImage: 'none',
          },
        },
      },
      // Menus, dialogs and panels are all part of the same workspace surface.
      // Chips and avatars deliberately keep their pill/circle geometry.
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: LENS_LAYOUT.cardRadius },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: LENS_LAYOUT.cardRadius,
            textTransform: 'none',
            fontWeight: 600,
            fontFamily: FONT_STACK_SUISSE_INTL,
          },
          // The admin primary is Ocean Teal in both modes. It is right on a
          // light canvas, but falls below contrast requirements against the
          // dark analytics field. Keep the semantic primary slot unchanged
          // (contained actions still need it), while giving quiet text and
          // outlined actions the mode-aware structural colour.
          textPrimary:
            base.palette.mode === 'dark'
              ? {
                  color: tokens.accent,
                  '&:hover': { backgroundColor: alpha(tokens.accent, 0.1) },
                }
              : undefined,
          outlinedPrimary:
            base.palette.mode === 'dark'
              ? {
                  borderColor: alpha(tokens.accent, 0.56),
                  color: tokens.accent,
                  '&:hover': {
                    backgroundColor: alpha(tokens.accent, 0.1),
                    borderColor: tokens.accent,
                  },
                }
              : undefined,
          // Keep palette.primary opaque for every MUI colour calculation. The
          // lighter treatment belongs only on actions sitting on a light canvas.
          containedPrimary:
            base.palette.mode === 'dark'
              ? undefined
              : {
                  backgroundColor: alpha(tokens.accent, 0.82),
                  '&:hover': { backgroundColor: alpha(tokens.accent, 0.9) },
                },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { padding: 16, '&:last-child': { paddingBottom: 16 } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: LENS_LAYOUT.inputRadius,
            ...(base.palette.mode === 'dark' ? { backgroundColor: 'transparent' } : {}),
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: { borderRadius: LENS_LAYOUT.cardRadius },
        },
      },
      // The legacy dark admin theme gives standard inputs a near-black fill.
      // Within this workspace they live inside an already-defined surface, so
      // that extra box breaks hierarchy and makes the composer look clipped.
      MuiInput: {
        styleOverrides: {
          root: base.palette.mode === 'dark' ? { backgroundColor: 'transparent' } : undefined,
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 500,
            fontFamily: FONT_STACK_SUISSE_INTL,
            minHeight: 28,
            '& .MuiChip-label': { paddingInline: 12 },
            '&.MuiChip-sizeSmall': {
              minHeight: 24,
              '& .MuiChip-label': { paddingInline: 10 },
            },
          },
        },
      },
      // Portal-rendered content (a tooltip popup, a menu, a select dropdown)
      // sits outside the provider's DOM subtree, so it cannot pick up type
      // from a scoped wrapper rule; it needs the family set directly here.
      MuiTooltip: {
        styleOverrides: {
          tooltip: { fontFamily: FONT_STACK_SUISSE_INTL },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { fontFamily: FONT_STACK_SUISSE_INTL },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { fontFamily: FONT_STACK_SUISSE_INTL },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: FONT_STACK_SUISSE_INTL,
            ...(base.palette.mode === 'dark' ? { backgroundColor: 'transparent' } : {}),
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { fontFamily: FONT_STACK_SUISSE_INTL },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: LENS_LAYOUT.cardRadius },
          // An information alert is a quiet note on a light surface, not a
          // solid primary action. MUI otherwise uses `info.contrastText`
          // (white) for this text, which is illegible on the pale info fill.
          standardInfo: {
            backgroundColor: alpha(tokens.accent, base.palette.mode === 'dark' ? 0.22 : 0.08),
            color: tokens.accent,
            '& .MuiAlert-icon': { color: tokens.accent },
          },
          outlinedInfo: {
            borderColor: alpha(tokens.accent, base.palette.mode === 'dark' ? 0.56 : 0.32),
            color: tokens.accent,
            '& .MuiAlert-icon': { color: tokens.accent },
          },
        },
      },
    },
  });
}
